import { VerityOptions, AssessmentInfo, PlatformInfo } from "@verity/api-client/types";
import { ThemeMessageSubject } from "../../types/theme-types";
import { LockdownLaunchInfo } from "../../portal/start-assessment";

export type AssessmentQueryInfo = {
	instituteId?: number;
	lmsProvidedCourseId?: string;
	lmsProvidedAssessmentId?: string;
	assessmentType?: "canvas-new-quiz" | "canvas-quiz";
};

type InstructionsInitInfo = {
	authLink?: string;
	quizLink?: string;
};

export function createThemeIframeHelper(): Promise<HTMLIFrameElement> {
	return new Promise((resolve, reject) => {
		const iframe = document.createElement("iframe");
		iframe.id = "yuja-verity-extension-connect";
		iframe.style.display = "none";
		iframe.style.visibility = "hidden";
		iframe.src = import.meta.env.VITE_VERITY_ORIGIN + "/resources/themes/extension-connect.html";
		document.body.appendChild(iframe);
		iframe.onload = () => resolve(iframe);
		iframe.onerror = reject;
		setTimeout(reject, 5000);
	});
}

export function sendInstituteId(iframe: HTMLIFrameElement, instituteId: number | undefined): Promise<boolean> {
	const targetOrigin = import.meta.env.VITE_VERITY_ORIGIN;
	return new Promise((resolve, reject) => {
		if (iframe.contentWindow && instituteId) {
			if (instituteId) {
				iframe.contentWindow.postMessage(
					{
						type: "saveInstituteId",
						instituteId: instituteId,
					},
					targetOrigin,
				);
				resolve(true);
			} else {
				resolve(false);
			}
		} else {
			reject();
		}
	});
}

export function checkExtension(iframe: HTMLIFrameElement): Promise<{ exists: boolean; version?: string }> {
	const targetOrigin = import.meta.env.VITE_VERITY_ORIGIN;
	return new Promise((resolve, reject) => {
		const listener = (ev: MessageEvent<unknown>) => {
			if (ev.origin !== targetOrigin) {
				console.error(`Blocked message from origin ${ev.origin}`);
				return;
			}
			const message = ev.data;
			if (
				message &&
				typeof message === "object" &&
				"require" in message &&
				message.require === "exists" &&
				"result" in message
			) {
				const exists = Boolean(message.result);
				const version = exists && "version" in message ? (message.version as string) : undefined;

				resolve({ exists, version });
				window.removeEventListener("message", listener);
			}
		};
		window.addEventListener("message", listener);

		if (iframe.contentWindow) {
			iframe.contentWindow.postMessage(
				{
					require: "exists",
				},
				targetOrigin,
			);
			setTimeout(() => {
				reject();
				window.removeEventListener("message", listener);
			}, 5000);
		} else {
			reject();
			window.removeEventListener("message", listener);
		}
	});
}

export function saveAssessmentUrl(
	iframe: HTMLIFrameElement,
	url: string,
	assessmentInfo: AssessmentInfo,
): Promise<boolean> {
	const targetOrigin = import.meta.env.VITE_VERITY_ORIGIN;
	return new Promise((resolve, reject) => {
		if (iframe.contentWindow) {
			iframe.contentWindow.postMessage(
				{
					type: "saveAssessmentUrl",
					url: url,
					assessmentInfo,
				},
				targetOrigin,
			);
			resolve(true);
		} else {
			reject();
		}
	});
}

export function saveMobileAssessmentUrl(iframe: HTMLIFrameElement, url: string): Promise<boolean> {
	const targetOrigin = import.meta.env.VITE_VERITY_ORIGIN;
	return new Promise((resolve, reject) => {
		if (iframe.contentWindow) {
			iframe.contentWindow.postMessage(
				{
					type: "saveMobileAssessmentUrl",
					url: url,
				},
				targetOrigin,
			);
			resolve(true);
		} else {
			reject();
		}
	});
}

async function sendMessage<T>(
	iframeHelper: HTMLIFrameElement,
	message: Record<string, unknown>,
	targetOrigin: string,
): Promise<T | undefined> {
	const requestId = crypto.randomUUID();
	return new Promise<T | undefined>((resolve, reject) => {
		const listener = (ev: MessageEvent<unknown>) => {
			if (ev.origin !== targetOrigin) {
				console.error(`Blocked message from origin ${ev.origin}`);
				return;
			}
			if (ev.data !== undefined) {
				const { payload, responseId } = ev.data as { payload: T; responseId: string };
				if (responseId === requestId) {
					resolve(payload);
					window.removeEventListener("message", listener);
				}
			}
		};

		window.addEventListener("message", listener);
		iframeHelper.contentWindow?.postMessage({ payload: message, requestId }, targetOrigin);
		setTimeout(() => {
			reject();
			window.removeEventListener("message", listener);
		}, 5000);
	}).catch(() => undefined);
}

export async function getLTIRef(iframeHelper: HTMLIFrameElement, instituteId: number) {
	return sendMessage<string>(iframeHelper, { type: "getLTIRef", instituteId }, import.meta.env.VITE_VERITY_ORIGIN);
}

export async function getInstituteMode(iframeHelper: HTMLIFrameElement, instituteId: number) {
	return sendMessage<{ liteMode: boolean }>(
		iframeHelper,
		{
			type: "getInstituteMode",
			instituteId,
		},
		import.meta.env.VITE_VERITY_ORIGIN,
	);
}

export async function copyCheck(
	iframeHelper: HTMLIFrameElement,
	instituteId: number,
	courseId: string,
	assessmentId: string,
	quizType?: "canvas-new-quiz" | "canvas-quiz",
) {
	return (
		(await sendMessage<boolean>(
			iframeHelper,
			{ type: "copyCheck", instituteId, courseId, assessmentId, quizType },
			import.meta.env.VITE_VERITY_ORIGIN,
		)) || false
	);
}

export async function isAssessmentVerityEnabled(
	iframeHelper: HTMLIFrameElement,
	instituteId: number,
	courseId: string,
	assessmentId: string,
) {
	return (
		(await sendMessage<boolean>(
			iframeHelper,
			{ type: "isAssessmentVerityEnabled", instituteId, courseId, assessmentId },
			import.meta.env.VITE_VERITY_ORIGIN,
		)) || false
	);
}

export async function isVerityLockdown(iframeHelper?: HTMLIFrameElement) {
	if (navigator.userAgent.includes("VerityLockdown")) {
		return true;
	}
	try {
		return !!window.top?.proctor;
	} catch (e) {
		if (iframeHelper) {
			try {
				return (
					(
						await sendMessage<PlatformInfo>(
							iframeHelper,
							{ type: "getPlatformInfo" },
							import.meta.env.VITE_VERITY_ORIGIN,
						)
					)?.platform === "lockdown"
				);
			} catch (e) {
				console.error(e);
			}
		}
		return false;
	}
}

const assessmentVerityStatus = new Map<string, Promise<boolean>>();

export async function isAssessmentVerityEnabledFromInfo({
	extensionConnectIframe,
	instituteId,
	lmsProvidedCourseId,
	lmsProvidedAssessmentId,
	assessmentType,
}: {
	extensionConnectIframe: HTMLIFrameElement;
	instituteId: number;
	lmsProvidedCourseId: string;
	lmsProvidedAssessmentId: string;
	assessmentType?: "canvas-new-quiz" | "canvas-quiz";
}) {
	const key = lmsProvidedCourseId + ":" + lmsProvidedAssessmentId + (assessmentType ? ":" + assessmentType : "");
	const storedEnabled = assessmentVerityStatus.get(key);
	if (storedEnabled) {
		return await storedEnabled;
	}

	const promise = (async () => {
		await copyCheck(extensionConnectIframe, instituteId, lmsProvidedCourseId, lmsProvidedAssessmentId, assessmentType);
		return await isAssessmentVerityEnabled(
			extensionConnectIframe,
			instituteId,
			lmsProvidedCourseId,
			lmsProvidedAssessmentId,
		);
	})();
	assessmentVerityStatus.set(key, promise);
	return await promise;
}

export async function getProctoringOptions(
	iframeHelper: HTMLIFrameElement,
	instituteId: number,
	courseId: string,
	assessmentId: string,
) {
	return (
		(await sendMessage<VerityOptions>(
			iframeHelper,
			{ type: "getProctoringOptions", instituteId, courseId, assessmentId },
			import.meta.env.VITE_VERITY_ORIGIN,
		)) || undefined
	);
}

export async function isVersionSupported(iframeHelper: HTMLIFrameElement, version: string) {
	return (
		(await sendMessage<boolean>(
			iframeHelper,
			{ type: "isVersionSupported", version },
			import.meta.env.VITE_VERITY_ORIGIN,
		)) || false
	);
}

export function isBrowserSupported() {
	return Boolean(window.chrome);
}

export const createModalIframe = (lockdownInfo: LockdownLaunchInfo) => {
	const modalDiv = document.createElement("div");
	modalDiv.style.display = "block";
	modalDiv.style.top = "0vh";
	modalDiv.style.zIndex = `99999999`;
	modalDiv.style.position = "fixed";

	const modalIframe = document.createElement("iframe");
	modalIframe.src = import.meta.env.VITE_VERITY_ORIGIN + "/launch/lockdownLaunch.html?url=" + window.location.href;
	modalIframe.style.display = "block";
	modalIframe.style.zIndex = `99999999`;
	modalIframe.style.width = "100vw";
	modalIframe.style.height = "100vh";
	modalIframe.style.overflow = "auto";
	modalIframe.style.position = "fixed";
	modalIframe.style.borderRadius = "17px";
	modalIframe.style.borderColor = "#BCBCBC";
	modalIframe.style.borderStyle = "solid";
	modalIframe.style.borderWidth = "1px";

	modalDiv.append(modalIframe);
	document.body.appendChild(modalDiv);

	window.addEventListener("message", (ev) => {
		if (ev.data?.request === "closeModalDiv") {
			modalDiv.remove();
		}
	});

	modalIframe.onload = () => {
		modalIframe.contentWindow?.postMessage(
			{
				event: "lockdown-info",
				lockdownInfo: lockdownInfo,
			},
			import.meta.env.VITE_VERITY_ORIGIN,
		);
	};
};

export const MOODLE_ATTRIBUTES = {
	ID: {
		QUIZ_PAGE: "page",
		QUIZ: "region-main",
		QUIZ_TITLE_INPUT: "id_name",
		SETTINGS: "region-main-box",
		QUIZ_POPUP_DIALOG_START: "id_submitbutton",
		QUIZ_POPUP_DIALOG_CANCEL: "id_cancel",
		PASSWORD_INPUT: "id_quizpassword",
		ADMIN_BLOCK_QUIZ_DIV: "adminsettings",
	},
	CLASS: {
		COURSE_ID_BODY_REGEX: /course-(\d+)/,
	},
	URL: {
		RELATIVE_COURSE_VIEW: "/course/view.php",
		RELATIVE_COURSE_VIEW_REGEX: /\/course\/view.php\?id=(\d+)/,
		RELATIVE_COURSE_ID_REGEX: /\/course\/.*\?id=(\d+)/,
		RELATIVE_QUIZ_VIEW_REGEX: /\/mod\/quiz\/view.php\?.*id=(\d+).*/,
		RELATIVE_QUIZ_SETTINGS_REGEX: /\/course\/modedit.php\?.*update=(\d+).*/,
		RELATIVE_QUIZ_ATTEMPT_REGEX: /\/attempt.php\?.*cmid=(\d+).*/,
		RELATIVE_QUIZ_START_ATTEMPT: "/mod/quiz/startattempt.php",
		RELATIVE_QUIZ_START_ATTEMPT_REGEX: /\/mod\/quiz\/startattempt.php\?.*cmid=(\d+).*/,
		RELATIVE_QUIZ_SUBMIT_ATTEMPT_REGEX: /\/mod\/quiz\/processattempt.php\?.*cmid=(\d+).*/,
		RELATIVE_QUIZ_REVIEW_ATTEMPT_REGEX: /\/mod\/quiz\/review.php\?.*cmid=(\d+).*/,
		RELATIVE_QUIZ_SUMMARY_REGEX: /\/mod\/quiz\/summary\.php\?.*cmid=(\d+)/,
		RELATIVE_THIRD_PARTY_QUIZ_VIEW_REGEX: /\/mod\/lti\/view\.php\?.*id=(\d+)/,
	},
};

export function getMoodleQuizInfo(url?: string): AssessmentInfo | null {
	if (typeof url === "string") {
		return getMoodleQuizInfoFromUrl(url);
	} else if (url === undefined) {
		return getMoodleQuizInfoFromDocument();
	}
	return null;
}

function getMoodleQuizInfoFromUrl(url: string): AssessmentInfo | null {
	const hostname = new URL(url).hostname;
	if (!hostname) {
		return null;
	}

	const quizId = getMoodleQuizId(url);
	if (!quizId) {
		return null;
	}

	return {
		lms: "moodle",
		hostname: hostname,
		quizId: quizId,
	};
}

export function getMoodleCourseIdFromUrl(url: string): string | null {
	let match = MOODLE_ATTRIBUTES.URL.RELATIVE_COURSE_VIEW_REGEX.exec(url);
	if (!match) {
		match = MOODLE_ATTRIBUTES.URL.RELATIVE_COURSE_ID_REGEX.exec(url);
	}
	return match?.[1] || null;
}

function getMoodleCourseIdFromClasslist(classlist: string): string | null {
	const match = MOODLE_ATTRIBUTES.CLASS.COURSE_ID_BODY_REGEX.exec(classlist);
	return match?.[1] || null;
}

function getMoodleQuizId(url: string): string | null {
	let match = MOODLE_ATTRIBUTES.URL.RELATIVE_QUIZ_VIEW_REGEX.exec(url);
	if (!match) {
		match = MOODLE_ATTRIBUTES.URL.RELATIVE_QUIZ_SETTINGS_REGEX.exec(url);
	}
	if (!match) {
		match = MOODLE_ATTRIBUTES.URL.RELATIVE_QUIZ_ATTEMPT_REGEX.exec(url);
	}
	if (!match) {
		match = MOODLE_ATTRIBUTES.URL.RELATIVE_QUIZ_START_ATTEMPT_REGEX.exec(url);
	}
	if (!match) {
		match = MOODLE_ATTRIBUTES.URL.RELATIVE_QUIZ_SUBMIT_ATTEMPT_REGEX.exec(url);
	}
	if (!match) {
		match = MOODLE_ATTRIBUTES.URL.RELATIVE_QUIZ_REVIEW_ATTEMPT_REGEX.exec(url);
	}
	if (!match) {
		match = MOODLE_ATTRIBUTES.URL.RELATIVE_QUIZ_SUMMARY_REGEX.exec(url);
	}
	if (!match) {
		match = MOODLE_ATTRIBUTES.URL.RELATIVE_THIRD_PARTY_QUIZ_VIEW_REGEX.exec(url);
	}
	return match?.[1] || null;
}

export function getMoodleQuizInfoFromDocument(): AssessmentInfo | null {
	if (!window?.location || !document) {
		return null;
	}

	const hostname = new URL(window.location.toString()).hostname;
	if (!hostname) {
		return null;
	}

	let courseId;
	const anchors: HTMLAnchorElement[] = Array.from(
		document.querySelectorAll(`#${MOODLE_ATTRIBUTES.ID.QUIZ_PAGE} nav a`),
	);
	let courseAnchors = anchors.filter((a) => MOODLE_ATTRIBUTES.URL.RELATIVE_COURSE_VIEW_REGEX.test(a.href));
	if (!courseAnchors?.length) {
		courseAnchors = anchors.filter((a) => MOODLE_ATTRIBUTES.URL.RELATIVE_COURSE_ID_REGEX.test(a.href));
	}
	if (courseAnchors?.length) {
		courseId = getMoodleCourseIdFromUrl(courseAnchors[0].href);
	} else {
		courseId = getMoodleCourseIdFromClasslist(document.body.className);
	}
	if (!courseId) {
		return null;
	}

	const quizId = getMoodleQuizId(window.location.toString());
	if (!quizId) {
		return null;
	}

	return {
		lms: "moodle",
		hostname: hostname,
		courseId: courseId,
		quizId: quizId,
	};
}

export function initializeInstructionsIframe(
	iframe: HTMLIFrameElement,
	getInitInfo: () => Promise<InstructionsInitInfo | undefined> | InstructionsInitInfo | undefined,
	targetOrigin = import.meta.env.VITE_VERITY_ORIGIN,
) {
	const sendInfo = async () => {
		const info = await getInitInfo();
		if (info) {
			console.debug("Sending 'init-auth-instructions' message.");
			iframe.contentWindow?.postMessage({ ...info, event: "init-auth-instructions" }, targetOrigin);
		}
	};

	iframe.addEventListener("load", sendInfo);
	const onMessage = (event: MessageEvent) => {
		if (event.data?.event === "loaded") {
			sendInfo();
			window.removeEventListener("message", onMessage);
		}
	};
	window.addEventListener("message", onMessage);
}

function removeExtensionIframes() {
	const frames = [
		document.getElementById("yuja-authentication-instructions"),
		document.getElementById("yuja-download-instructions"),
	];
	frames.forEach((frame) => frame?.remove());
}

export function observeAndRemoveExtensionIframes() {
	removeExtensionIframes();
	const observer = new MutationObserver(async (mutationsList) => {
		for (const mutation of mutationsList) {
			if (mutation.type === "childList") {
				removeExtensionIframes();
			}
		}
	});
	observer.observe(document.body, { childList: true, subtree: true });
}

export function listenForLTILoad(lms: string) {
	const onMessage = (event: MessageEvent) => {
		if (!event.data || event.origin !== import.meta.env.VITE_VERITY_ORIGIN) return;

		if (event.data.subject === ThemeMessageSubject.ltiLoaded) {
			event.source?.postMessage(
				{
					subject: ThemeMessageSubject.themeExists,
					lms: lms,
				},
				{ targetOrigin: event.origin },
			);
			// Don't remove event listener here, causes the theme to fail to respond when the user authenticates themselves with Canvas via OAuth
		}
	};

	window.addEventListener("message", onMessage);
}
